import React from 'react';
import Select, { components } from 'react-select';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import Icon from '../Icon';

import s from './OverviewSelect.module.scss';
import Image from '../Image';

const OverviewSelect = ({
    value,
    placeholder,
    options,
    handleChange,
    isClearable = false,
    modifier,
    hasBorder,
    isMediaMonitorFilter = false,
}) => {
    if (!options) {
        return null;
    }

    const selectedOption = options.find(
        (option) => String(option.value) === String(value)
    );

    const SingleValue = (props) => {
        const { selectProps } = props;
        const isMenuOpen = selectProps.menuIsOpen;

        return (
            <components.SingleValue {...props}>
                <div className={s.IconTextWrapper}>
                    {!isMenuOpen && (
                        <Icon name="Search" className={s.IconLeft} />
                    )}
                    <div className={s.SingleValueButton}>{props.children}</div>
                </div>
            </components.SingleValue>
        );
    };

    const Placeholder = (props) => {
        const { selectProps } = props;
        const isMenuOpen = selectProps.menuIsOpen;

        return (
            <components.Placeholder {...props}>
                <div className={s.IconTextWrapper}>
                    {!isMenuOpen && (
                        <Icon name="Search" className={s.IconLeft} />
                    )}
                    {props.children}
                </div>
            </components.Placeholder>
        );
    };

    const Menu = (props) => {
        return (
            <components.Menu {...props} className={s.Menu}>
                {props.children}
            </components.Menu>
        );
    };

    const ClearIndicator = (props) => {
        return (
            <components.ClearIndicator {...props}>
                <div className={s.ClearIcon}>
                    <Icon name="Close" />
                </div>
            </components.ClearIndicator>
        );
    };

    const OptionWithImage = (props) => {
        const classes = classNames(s.SelectOptionWithImage, {
            [s['SelectOptionWithImage--IsSelected']]: props.isSelected,
        });

        return (
            <components.Option {...props}>
                <div className={classes}>
                    {props.data.image && (
                        <div className={s.Image}>
                            <Image {...props.data.image} />
                        </div>
                    )}
                    <div className={s.Text}>
                        <div className={s.Title}>{props.data.label}</div>
                        <div className={s.Date}>{props.data.publishedDate}</div>
                    </div>
                </div>
            </components.Option>
        );
    };

    const Option = (props) => {
        return (
            <components.Option {...props}>{props.children}</components.Option>
        );
    };

    const sortedOptions = [...options].sort((a, b) => {
        const aLabel = typeof a.label === 'string' ? a.label : '';
        const bLabel = typeof b.label === 'string' ? b.label : '';
        return aLabel.localeCompare(bLabel);
    });

    return (
        <div className={s.Root}>
            <Select
                classNames={{
                    control: ({ isFocused, selectProps }) =>
                        classNames(s.SelectControl, {
                            [s[`SelectControl--${modifier}`]]: modifier,
                            [s['SelectControl--IsFocused']]: isFocused,
                            [s['SelectControl--HasBorder']]: hasBorder,
                            [s['SelectControl--IsMenuOpened']]:
                                selectProps.menuIsOpen,
                        }),
                    input: () => s.SelectInput,
                    singleValue: () => s.SelectSingleValue,
                    option: ({ isSelected }) => {
                        return classNames(s.SelectOption, {
                            [s['SelectOption--IsSelected']]: isSelected,
                        });
                    },
                    menuList: () => s.MenuList,
                    noOptionsMessage: () => s.SelectNoOptionsMessage,
                    menu: () => s.SelectMenu,
                    placeholder: () => s.SelectPlaceholder,
                    clearIndicator: () => s.ClearIndicator,
                }}
                components={{
                    DropdownIndicator: (opts) => {
                        const isMenuOpen = opts?.selectProps?.menuIsOpen;

                        return (
                            <div className={s.IconRight}>
                                {isMenuOpen && <Icon name="Search" />}
                            </div>
                        );
                    },
                    IndicatorSeparator: () => null,
                    ClearIndicator,
                    SingleValue, // Use custom SingleValue component
                    Placeholder, // Use custom Placeholder component
                    Menu,
                    Option: isMediaMonitorFilter ? OptionWithImage : Option,
                }}
                isClearable={isClearable}
                placeholder={placeholder}
                onChange={handleChange}
                options={sortedOptions}
                value={selectedOption || null}
            />
        </div>
    );
};

OverviewSelect.propTypes = {
    value: PropTypes.string,
    options: PropTypes.arrayOf(
        PropTypes.shape({
            value: PropTypes.string,
            name: PropTypes.string,
        })
    ),
    handleChange: PropTypes.func,
    isClearable: PropTypes.bool,
    modifier: PropTypes.string,
};

export default OverviewSelect;
